import clsx from 'clsx';
import dot from 'dot-object';
import { useEffect, useState } from 'react';
import useOdinMemo from '../../../common/hooks/use-odin-memo';
import { forceAssert, isCheckboxGroupResult } from '../../common-functions';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import type { SelectButtonChangeEvent } from 'primereact/selectbutton';
import { SelectButton as PrimeReactSelectButton } from 'primereact/selectbutton';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { CheckboxGroupResult } from '../checkbox-group/checkbox-group.component';

export interface SelectButtonProps
  extends DynamicFieldComponentProps<Array<CheckboxGroupResult>>,
    DynamicFieldDataProps {
  showError?: boolean;
}

const SelectButton = ({
  label,
  value = [],
  data,
  textField,
  valueField,
  inputRef,
  onChange,
  readOnly,
  required,
  error,
  disabled,
  showError = true,
}: SelectButtonProps) => {
  const [selectedValues, setSelectedValues] = useState(value?.map((item) => item.value));
  const tagCreator = useTestTag(label || 'select-button');

  const optionData = useOdinMemo(
    () =>
      forceAssert<JsonData>(data).map<CheckboxGroupResult>((item) => {
        return {
          value: dot.pick(valueField, item),
          text: dot.pick(textField, item),
        };
      }),
    [data]
  );

  useEffect(() => {
    setSelectedValues(value?.map((item) => item.value));
  }, [value]);

  const handleChange = (event: SelectButtonChangeEvent) => {
    const values = event.value as Array<unknown>;
    const selectedObjects = values
      .map<CheckboxGroupResult>((item: CheckboxGroupResult | unknown) => {
        if (isCheckboxGroupResult(item)) {
          return item;
        } else {
          const foundOption = forceAssert<JsonData>(data).find((option) => dot.pick(valueField, option) === item);
          if (foundOption) {
            return {
              value: item,
              text: dot.pick(textField, foundOption),
            };
          }

          return {
            value: null,
            text: null,
          };
        }
      })
      .filter((option) => option.value !== null && option.text !== null);

    onChange?.(selectedObjects);
    setSelectedValues(selectedObjects.map((item) => item.value));
  };

  const commonButtonStyles = clsx(
    'odin-sm:w-auto odin-text-md odin-leading-5 odin-rounded focus:odin-outline-none odin-justify-center odin-transition-colors odin-duration-200 odin-min-w-[3rem] odin-min-h-[2.25rem] odin-py-2 odin-px-5 odin-mr-4'
  );

  const customOptionTemplate = (option: JsonDataItem) => {
    const optionValue = dot.pick('value', option);
    const optionText = dot.pick('text', option) as string;
    const isSelected = selectedValues?.findIndex((item) => item === optionValue) > -1;

    //different styles depending on selected or not
    const buttonStyles = clsx(
      commonButtonStyles,
      {
        'odin-min-w-5 odin-h-9 odin-rounded odin-bg-gray-4 odin-px-2.5 odin-text-sm': !isSelected,
      },
      { 'odin-min-w-5 odin-h-9 odin-rounded odin-bg-primary-2 odin-text-mono-1 odin-px-2.5 odin-text-sm': isSelected },
      { 'odin-opacity-50': readOnly || disabled }
    );

    return (
      <button
        value={optionValue as string}
        className={buttonStyles}
        ref={tagCreator(optionText?.replace(/\s+/g, '-').toLowerCase())}
      >
        {optionText}
      </button>
    );
  };

  return (
    <>
      <div className="odin-flex odin-items-center odin-gap-2 odin-p-1">
        <RequiredIndicator readOnly={readOnly} required={required} />
        <PrimeReactSelectButton
          onChange={handleChange}
          value={selectedValues}
          itemTemplate={customOptionTemplate}
          options={optionData}
          optionLabel={textField}
          optionValue="value"
          disabled={readOnly || disabled}
          ref={inputRef}
          multiple
          className="odin-flex odin-flex-col odin-space-y-2"
        />
      </div>
      {error && showError && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
};

export default SelectButton;
